
import { useState, useEffect } from "react";
import { useCart } from "@/contexts/CartContext";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { CartItems } from "@/components/cart/CartItems";
import { CartSummary } from "@/components/cart/CartSummary";
import { EmptyCart } from "@/components/cart/EmptyCart";
import { CartAlerts } from "@/components/cart/CartAlerts";

const Cart = () => {
  const { items, removeItem, updateQuantity, total, clearCart } = useCart();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [session, setSession] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const validateCartItems = async () => {
    if (!session) return false;

    const currentUserId = session.user.id;
    const invalidItems = items.filter(item => !item.chef_id);
    const ownMeals = items.filter(item => item.chef_id === currentUserId);

    if (invalidItems.length > 0) {
      toast({
        title: "Invalid cart items",
        description: "Some items are missing required information. Please remove them and try again.",
        variant: "destructive",
      });
      return false;
    }

    if (ownMeals.length > 0) {
      toast({
        title: "Invalid cart items",
        description: "You cannot purchase your own meals. Please remove them and try again.",
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handlePayment = async () => {
    try {
      setIsLoading(true);
      
      if (!session) {
        toast({
          title: "Authentication required",
          description: "Please sign in to proceed with checkout",
          variant: "destructive",
        });
        navigate("/auth");
        return;
      }
      
      const isValid = await validateCartItems();
      if (!isValid) {
        setIsLoading(false);
        return;
      }

      console.log('Processing payment for items:', items);
      navigate("/checkout");
      
    } catch (error) {
      console.error('Error during payment:', error);
      toast({
        title: "Payment Error",
        description: "There was a problem processing your payment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const hasUnscheduledItems = items.some(item => !item.scheduledDate || !item.scheduledTime);
  const isCheckoutDisabled = hasUnscheduledItems || items.some(item => !item.chef_id) || !session || isLoading;

  if (items.length === 0) {
    return <EmptyCart />;
  }

  return (
    <div className="min-h-screen bg-background py-8 sm:py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="space-y-6">
          <CartAlerts session={session} items={items} />

          <CartItems
            items={items}
            onUpdateQuantity={updateQuantity}
            onRemoveItem={removeItem}
          />

          <CartSummary
            total={total}
            onCheckout={handlePayment}
            isLoading={isLoading}
            isCheckoutDisabled={isCheckoutDisabled}
          />
        </div>
      </div>
    </div>
  );
};

export default Cart;
